import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { RadioTowerIcon, SmartphoneIcon, MonitorIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const IOT_IMAGE = 'https://cdn.magicpatterns.com/uploads/aRpNDYQvXkxhUWJMfM96DP/IoT_with_mobilizer.png';
const DRIVER_APP_IMAGE = 'https://cdn.magicpatterns.com/uploads/qgvmz7JfwXaVx3XEaQcEmt/image.png';
const WEB_PORTAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/7mS9PFY7VGXZNzGwrSF3Rj/image.png';
const COMPONENTS = [{
  id: 'iot',
  titleAr: 'جهاز التتبع والتعطيل',
  titleEn: 'IoT Device & Immobilizer',
  icon: RadioTowerIcon,
  image: IOT_IMAGE,
  imageStyle: 'object-contain' as const,
  bullets: ['تتبع GPS مباشر', 'تعطيل المركبة عن بُعد', 'مراقبة حالة المحرك']
}, {
  id: 'app',
  titleAr: 'تطبيق السائق',
  titleEn: 'Driver Mobile App',
  icon: SmartphoneIcon,
  image: DRIVER_APP_IMAGE,
  imageStyle: 'object-contain' as const,
  bullets: ['تسجيل دخول السائق', 'إثبات الهوية', 'استلام المهام والتنبيهات']
}, {
  id: 'portal',
  titleAr: 'البوابة الإلكترونية',
  titleEn: 'Web Portal',
  icon: MonitorIcon,
  image: WEB_PORTAL_IMAGE,
  imageStyle: 'object-contain' as const,
  bullets: ['لوحة تحكم شاملة', 'تقارير وتحليلات', 'إدارة الأساطيل والسائقين']
}];
export function EcosystemSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-281">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-282">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.04) 0%, transparent 55%)'
      }} data-id="element-283" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v48H0V0zm1 1h46v46H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-284" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-285">
        <SharjahPoliceEmblem variant="light" data-id="element-286" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-287" />
      </motion.div>

      {/* ===== TITLE ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.2
    }} className="relative z-10 px-[clamp(2rem,6vw,6rem)] mt-[clamp(0.5rem,1.5vh,1rem)]" data-id="element-288">
        <h2 className="font-arabic-display font-black text-center leading-[1.15]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.4rem, 2.3vw, 2.1rem)'
      }} data-id="element-289">
          كيف تعمل منظومة{' '}
          <span style={{
          color: '#2FA99C'
        }} data-id="element-290">
            Pair²
          </span>
        </h2>
        <div className="mx-auto mt-2.5 h-[2.5px] rounded-full" style={{
        width: 'clamp(3rem, 5vw, 4rem)',
        background: '#2FA99C',
        opacity: 0.45
      }} data-id="element-291" />
      </motion.div>

      {/* ===== 3 COMPONENT CARDS ===== */}
      <div className="relative z-10 flex-1 min-h-0 flex items-center justify-center px-[clamp(1.5rem,4vw,4rem)] pt-[clamp(0.5rem,1.5vh,1rem)] pb-[clamp(0.5rem,1vh,0.75rem)]" data-id="element-292">
        <div className="w-full grid gap-[clamp(1rem,2vw,2rem)]" style={{
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
      }} data-id="element-293">
          {COMPONENTS.map((comp, i) => {
          const Icon = comp.icon;
          return <motion.div key={comp.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={base ?? {
            duration: 0.7,
            delay: 0.35 + i * 0.15,
            type: 'spring',
            bounce: 0.25
          }} className="flex flex-col rounded-xl overflow-hidden" style={{
            background: 'rgba(255,255,255,0.88)',
            border: '1px solid rgba(47,169,156,0.1)',
            boxShadow: '0 8px 28px -8px rgba(14,93,85,0.08)'
          }} data-id="element-294">
                {/* Image area */}
                <div className="relative w-full flex items-center justify-center overflow-hidden" style={{
              height: 'clamp(10rem, 28vh, 16rem)',
              background: comp.id === 'iot' ? '#f8f9fb' : '#f4f6f8'
            }} data-id="element-295">
                  <img src={comp.image} alt={comp.titleEn} draggable={false} className={`w-full h-full select-none ${comp.imageStyle}`} style={{
                padding: comp.id === 'iot' ? 'clamp(0.75rem, 1.5vw, 1.25rem)' : '0'
              }} data-id="element-296" />
                </div>

                {/* Content area */}
                <div className="flex flex-col p-[clamp(0.75rem,1.5vw,1.25rem)]" data-id="element-297">
                  {/* Header with icon + titles */}
                  <div className="flex items-center gap-[clamp(0.4rem,0.6vw,0.5rem)] mb-[clamp(0.5rem,1.2vh,0.75rem)]" dir="rtl" data-id="element-298">
                    <div className="shrink-0 rounded-lg flex items-center justify-center" style={{
                  width: 'clamp(1.75rem, 2.5vw, 2.25rem)',
                  height: 'clamp(1.75rem, 2.5vw, 2.25rem)',
                  background: 'rgba(47,169,156,0.08)',
                  border: '1px solid rgba(47,169,156,0.18)'
                }} data-id="element-299">
                      <Icon style={{
                    width: 'clamp(0.85rem, 1.1vw, 1rem)',
                    height: 'clamp(0.85rem, 1.1vw, 1rem)',
                    color: '#2FA99C'
                  }} strokeWidth={1.8} data-id="element-300" />
                    </div>
                    <div className="min-w-0" data-id="element-301">
                      <h3 className="font-arabic-display font-bold" style={{
                    color: '#0B1328',
                    fontSize: 'clamp(0.85rem, 1.1vw, 1.05rem)',
                    lineHeight: 1.3
                  }} data-id="element-302">
                        {comp.titleAr}
                      </h3>
                      <span className="font-arabic-body" dir="ltr" style={{
                    color: '#6E7A8F',
                    fontSize: 'clamp(0.65rem, 0.8vw, 0.75rem)'
                  }} data-id="element-303">
                        {comp.titleEn}
                      </span>
                    </div>
                  </div>

                  {/* Bullet points */}
                  <div className="flex flex-col gap-[clamp(0.3rem,0.7vh,0.45rem)]" data-id="element-304">
                    {comp.bullets.map((bullet, j) => <div key={j} className="flex items-center gap-[clamp(0.3rem,0.4vw,0.35rem)]" dir="rtl" data-id="element-305">
                        <span className="shrink-0 w-[5px] h-[5px] rounded-full" style={{
                    background: '#2FA99C',
                    opacity: 0.6
                  }} data-id="element-306" />
                        <span className="font-arabic-body" style={{
                    color: '#1A1F2B',
                    fontSize: 'clamp(0.7rem, 0.85vw, 0.82rem)',
                    opacity: 0.8
                  }} data-id="element-307">
                          {bullet}
                        </span>
                      </div>)}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* ===== BOTTOM SPACER ===== */}
      <div className="h-[clamp(1rem,2vh,1.5rem)]" data-id="element-308" />
    </section>;
}